// Lista de arquivos (localStorage) + cache de HTML (IndexedDB).

import type { DriveFile } from "./drive-api";

const LIST_KEY = "drive-viewer.list.v1";
const DB_NAME = "drive-viewer";
const DB_STORE = "html-cache";

export type TrackedFile = {
  id: string;
  name: string;
  modifiedTime: string;
  addedAt: number;
};

type Listener = (list: TrackedFile[]) => void;
const listeners = new Set<Listener>();

function readList(): TrackedFile[] {
  try {
    const raw = localStorage.getItem(LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrackedFile[]) : [];
  } catch {
    return [];
  }
}

function writeList(list: TrackedFile[]): void {
  localStorage.setItem(LIST_KEY, JSON.stringify(list));
  for (const fn of listeners) {
    try {
      fn(list);
    } catch (err) {
      console.error("[store] listener threw", err);
    }
  }
}

export function getList(): TrackedFile[] {
  return readList();
}

export function getFile(id: string): TrackedFile | null {
  return readList().find((f) => f.id === id) ?? null;
}

export function onListChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function addFromDrive(file: DriveFile): TrackedFile {
  const list = readList();
  const existing = list.find((f) => f.id === file.id);
  if (existing) {
    existing.name = file.name;
    existing.modifiedTime = file.modifiedTime;
    writeList(list);
    return existing;
  }
  const tracked: TrackedFile = {
    id: file.id,
    name: file.name,
    modifiedTime: file.modifiedTime,
    addedAt: Date.now(),
  };
  list.unshift(tracked);
  writeList(list);
  return tracked;
}

export function updateModifiedTime(id: string, modifiedTime: string, name?: string): void {
  const list = readList();
  const entry = list.find((f) => f.id === id);
  if (!entry) return;
  entry.modifiedTime = modifiedTime;
  if (name) entry.name = name;
  writeList(list);
}

export function remove(id: string): void {
  const list = readList().filter((f) => f.id !== id);
  writeList(list);
  // Limpa cache também — fire and forget.
  void deleteCachedHtml(id);
}

// ---------------- IndexedDB cache ----------------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
  });
  return dbPromise;
}

export type CachedHtml = { html: string; modifiedTime: string; fetchedAt: number };

export async function getCachedHtml(id: string): Promise<CachedHtml | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readonly");
    const req = tx.objectStore(DB_STORE).get(id);
    req.onsuccess = () => resolve((req.result as CachedHtml | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function setCachedHtml(id: string, value: CachedHtml): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).put(value, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteCachedHtml(id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("[store] cache delete failed", err);
  }
}
